// Error tracking analytics events

export type ErrorCategory =
  | 'api_error'
  | 'validation_error'
  | 'client_error'
  | 'network_error'
  | 'auth_error'
  | 'feature_error';

export const errorEvents = {
  apiError: (
    endpoint: string,
    statusCode: number,
    errorMessage: string,
    context?: Record<string, unknown>
  ) => ({
    event: 'error_api',
    properties: {
      endpoint,
      status_code: statusCode,
      error_message: errorMessage,
      error_category: getHttpErrorCategory(statusCode),
      ...context,
    },
  }),

  validationError: (
    form: string,
    field: string,
    errorType: string,
    errorMessage?: string
  ) => ({
    event: 'error_validation',
    properties: {
      form,
      field,
      error_type: errorType,
      error_message: errorMessage,
    },
  }),

  clientError: (
    errorName: string,
    errorMessage: string,
    componentStack?: string,
    url?: string
  ) => ({
    event: 'error_client',
    properties: {
      error_name: errorName,
      error_message: errorMessage,
      component_stack: typeof componentStack === 'string' ? componentStack.substring(0, 1000) : undefined,
      url: url || (typeof window !== 'undefined' ? window.location.href : undefined),
    },
  }),

  networkError: (
    requestUrl: string,
    errorMessage: string,
    retryCount?: number
  ) => ({
    event: 'error_network',
    properties: {
      request_url: requestUrl,
      error_message: errorMessage,
      retry_count: retryCount,
    },
  }),

  authError: (
    action: string,
    errorMessage: string,
    errorCode?: string
  ) => ({
    event: 'error_auth',
    properties: {
      action,
      error_message: errorMessage,
      error_code: errorCode,
    },
  }),

  featureError: (
    feature: string,
    errorType: string,
    errorMessage: string,
    context?: Record<string, unknown>
  ) => ({
    event: 'error_feature',
    properties: {
      feature,
      error_type: errorType,
      error_message: errorMessage,
      ...context,
    },
  }),

  formSubmitError: (
    formName: string,
    errorMessage: string,
    fieldsWithErrors: string[]
  ) => ({
    event: 'error_form_submit',
    properties: {
      form_name: formName,
      error_message: errorMessage,
      fields_with_errors: fieldsWithErrors,
      error_count: fieldsWithErrors.length,
    },
  }),

  edgeFunctionError: (
    functionName: string,
    errorMessage: string,
    durationMs?: number
  ) => ({
    event: 'error_edge_function',
    properties: {
      function_name: functionName,
      error_message: errorMessage,
      duration_ms: durationMs,
    },
  }),

  supabaseError: (
    operation: string,
    table: string,
    errorMessage: string,
    errorCode?: string
  ) => ({
    event: 'error_supabase',
    properties: {
      operation,
      table,
      error_message: errorMessage,
      error_code: errorCode,
    },
  }),

  stripeError: (
    action: string,
    errorMessage: string,
    errorCode?: string,
    declineCode?: string
  ) => ({
    event: 'error_stripe',
    properties: {
      action,
      error_message: errorMessage,
      error_code: errorCode,
      decline_code: declineCode,
    },
  }),

  unhandledError: (
    errorMessage: string,
    errorStack?: string,
    source?: string
  ) => ({
    event: 'error_unhandled',
    properties: {
      error_message: errorMessage,
      error_stack: typeof errorStack === 'string' ? errorStack.substring(0, 1000) : undefined,
      source,
    },
  }),

  promiseRejection: (
    reason: string,
    errorStack?: string
  ) => ({
    event: 'error_promise_rejection',
    properties: {
      reason,
      error_stack: typeof errorStack === 'string' ? errorStack.substring(0, 1000) : undefined,
    },
  }),

  uiError: (
    component: string,
    errorMessage: string,
    context?: Record<string, unknown>
  ) => ({
    event: 'error_ui',
    properties: {
      component,
      error_message: errorMessage,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...context,
    },
  }),
};

function getHttpErrorCategory(statusCode: number): string {
  if (statusCode >= 500) return 'server_error';
  if (statusCode === 429) return 'rate_limit';
  if (statusCode === 401) return 'unauthorized';
  if (statusCode === 403) return 'forbidden';
  if (statusCode === 404) return 'not_found';
  if (statusCode === 400) return 'bad_request';
  if (statusCode === 422) return 'validation_error';
  if (statusCode >= 400) return 'client_error';
  return 'unknown';
}

export type ErrorEvent = ReturnType<typeof errorEvents[keyof typeof errorEvents]>;
